/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { Kind, parse, print } from "graphql";

/**
 * Regex that matches a GraphQL *fragment definition* header (`fragment Name on Type`).
 * It deliberately does NOT match fragment *spreads* (`...Name`) because those have no
 * `on Type` clause — so it counts only definitions, which is what may legally repeat.
 */
const FRAGMENT_DEFINITION_RE = /\bfragment\s+([A-Za-z_][A-Za-z0-9_]*)\s+on\b/g;

/**
 * Remove duplicate fragment *definitions* from a GraphQL query string, keeping the first
 * occurrence of each fragment name.
 *
 * The generated `typescript-graphql-request` SDK builds every query `Document` by
 * string-concatenating the `FragmentDoc` constants it references, and each of those
 * `FragmentDoc`s in turn concatenates its own transitive dependencies. When a fragment is
 * shared across the tree — e.g. `CosmosButton`, pulled in independently by `SectionHeading`,
 * `CosmosModButton`, and the section fragment itself — it ends up defined multiple times in
 * a single document, and Hygraph rejects the request with
 * `There can be only one fragment named "…"`.
 *
 * graphql-codegen's `dedupeFragments` option only collapses duplicate fragment *spreads*
 * within a selection set; it does not deduplicate the definition text produced by these
 * nested `FragmentDoc` interpolations. So we dedupe definitions here, at request time,
 * where the fully-assembled document is available.
 *
 * The cheap regex pre-scan avoids paying the parse/print cost (and any incidental
 * reformatting) on the overwhelming majority of documents that contain no duplicates.
 */
export function dedupeQueryFragments(query: string): string {
	const seen = new Set<string>();
	let hasDuplicate = false;
	for (const match of query.matchAll(FRAGMENT_DEFINITION_RE)) {
		const name = match[1];
		if (seen.has(name)) {
			hasDuplicate = true;
			break;
		}
		seen.add(name);
	}

	if (!hasDuplicate) {
		return query;
	}

	const document = parse(query);
	const keptFragments = new Set<string>();
	const definitions = document.definitions.filter((definition) => {
		if (definition.kind !== Kind.FRAGMENT_DEFINITION) {
			return true;
		}
		if (keptFragments.has(definition.name.value)) {
			return false;
		}
		keptFragments.add(definition.name.value);
		return true;
	});

	return print({ ...document, definitions });
}

/**
 * Parse a graphql-request POST body, dedupe fragment definitions in the contained query
 * (single or batched), and return the re-serialized body. Returns the original string
 * unchanged when it isn't JSON we recognize or when nothing needed deduping, so the hook is
 * safe to run on every request.
 */
export function dedupeRequestBody(body: string): string {
	let parsed: unknown;
	try {
		parsed = JSON.parse(body);
	} catch {
		return body;
	}

	let changed = false;
	const dedupeEntry = (entry: unknown): unknown => {
		if (entry && typeof entry === "object" && typeof (entry as { query?: unknown }).query === "string") {
			const original = (entry as { query: string }).query;
			const deduped = dedupeQueryFragments(original);
			if (deduped !== original) {
				changed = true;
				return { ...entry, query: deduped };
			}
		}
		return entry;
	};

	const next = Array.isArray(parsed) ? parsed.map(dedupeEntry) : dedupeEntry(parsed);
	return changed ? JSON.stringify(next) : body;
}
